import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import { features, stats, categories, articles } from "@/mocks/data";

function StatItem({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{value}</div>
      <div className="text-sm text-foreground-600 mt-1">{label}</div>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="group p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
      <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
        <i className={`${icon} text-xl`}></i>
      </div>
      <h3 className="text-lg font-semibold text-foreground-900 mb-2 font-heading">{title}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed">{description}</p>
    </div>
  );
}

function CategoryCard({ category }: { category: typeof categories[0] }) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => {
        navigate(`/category/${category.slug}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      className="group w-full text-left p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:shadow-sm transition-all duration-300 cursor-pointer"
    >
      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors duration-300">
        <i className={`${category.icon} text-lg`}></i>
      </div>
      <h3 className="text-base font-semibold text-foreground-900 mb-1">{category.name}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed mb-3">{category.description}</p>
      <div className="flex items-center gap-1 text-xs text-accent-600">
        <span>{category.articleCount} 篇资讯</span>
        <i className="ri-arrow-right-line"></i>
      </div>
    </button>
  );
}

function ArticleCard({ article }: { article: typeof articles[0] }) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => {
        navigate(`/article/${article.id}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      className="group w-full text-left flex flex-col sm:flex-row gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="w-full sm:w-48 h-36 sm:h-28 rounded-lg overflow-hidden shrink-0">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={article.title}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-50 text-accent-600">
            {article.categoryName}
          </span>
          <span className="text-xs text-foreground-400">{article.publishDate}</span>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
      </div>
    </button>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "1996至成国际",
    url: "https://www.1996zhcheng.com",
    description: t("seo.home.description"),
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.1996zhcheng.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "1996至成国际",
    url: "https://www.1996zhcheng.com",
    description: t("seo.home.description"),
    foundingDate: "1996",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+852-2888-8888",
      contactType: "customer service",
      availableLanguage: ["Chinese", "English"],
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "中环皇后大道中99号中环中心58楼",
      addressLocality: "香港",
      addressCountry: "HK",
    },
  };

  return (
    <>
      <SEOHead
        title={t("seo.home.title")}
        description={t("seo.home.description")}
        keywords={t("seo.home.keywords")}
        canonicalUrl="https://www.1996zhcheng.com"
        schemaJson={[websiteSchema, orgSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20modern%20abstract%20geometric%20composition%20with%20warm%20golden%20and%20cream%20gradient%20tones%2C%20soft%20flowing%20shapes%2C%20luxurious%20minimal%20design%2C%20professional%20corporate%20atmosphere%2C%20clean%20smooth%20lines%2C%20ethereal%20light%20effects%2C%20no%20text%2C%20elegant%20and%20sophisticated%20background&width=1920&height=1080&seq=hero-bg-main&orientation=landscape"
            alt="1996至成国际背景"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

          <div className="relative z-10 w-full container-page text-center py-20 md:py-28">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-6 md:mb-8">
              <i className="ri-verified-badge-line text-accent-400 text-sm"></i>
              <span className="text-background-50/90 text-sm font-medium">官方正版 · 安全可靠</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 font-heading mb-4 md:mb-6 leading-tight">
              {t("home.hero.title")}
            </h1>
            <p className="text-lg md:text-2xl text-background-50/90 font-heading mb-4">
              {t("home.hero.subtitle")}
            </p>
            <p className="text-sm md:text-base text-background-50/70 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
              {t("home.hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-download-2-line text-lg"></i>
                {t("cta.download_now")}
              </button>
              <button
                onClick={() => {
                  navigate("/about");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="w-full sm:w-auto px-8 py-3.5 rounded-full border border-background-50/40 text-background-50 font-medium text-base hover:bg-background-50/10 transition-all cursor-pointer whitespace-nowrap"
              >
                {t("cta.learn_more")}
              </button>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative -mt-10 z-20">
          <div className="container-page">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70">
              {stats.map((stat) => (
                <StatItem key={stat.id} value={stat.value} label={stat.label} />
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 md:py-24">
          <div className="container-page">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
                {t("home.features.title")}
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                {t("home.features.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => (
                <FeatureCard key={feature.id} {...feature} />
              ))}
            </div>
          </div>
        </section>

        {/* Download CTA Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="container-page">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
              <div className="flex-1 text-center lg:text-left">
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
                  {t("home.download.title")}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mb-8 leading-relaxed">
                  {t("home.download.subtitle")}
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                  <button className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-foreground-900 text-background-50 font-semibold text-base hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-apple-fill text-lg"></i>
                    App Store 下载
                  </button>
                  <button className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-android-fill text-lg"></i>
                    Android 下载
                  </button>
                </div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="relative w-64 h-[420px] md:w-72 md:h-[480px] rounded-3xl overflow-hidden border-4 border-background-200/70 bg-background-50">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20smartphone%20app%20interface%20mockup%20displaying%20a%20professional%20international%20service%20platform%20dashboard%20with%20clean%20minimal%20design%2C%20warm%20golden%20and%20cream%20color%20scheme%2C%20elegant%20UI%20elements%2C%20data%20visualization%20cards%2C%20smooth%20rounded%20corners%2C%20luxurious%20business%20app%20aesthetic%2C%20on%20a%20clean%20light%20background&width=600&height=1200&seq=app-mockup-home&orientation=portrait"
                    alt="1996至成国际APP界面预览"
                    className="w-full h-full object-cover object-top"
                    title="1996至成国际APP界面"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 md:py-24">
          <div className="container-page">
            <div className="flex flex-col sm:flex-row items-end justify-between mb-10 md:mb-14">
              <div>
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
                  {t("home.categories.title")}
                </h2>
                <p className="text-sm text-foreground-600">{t("home.categories.subtitle")}</p>
              </div>
              <button
                onClick={() => {
                  navigate("/category/cross-border-business");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="mt-4 sm:mt-0 px-5 py-2.5 rounded-full border border-foreground-300 text-foreground-600 text-sm font-medium hover:border-primary-500 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                {t("cta.view_all")}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <CategoryCard key={cat.id} category={cat} />
              ))}
            </div>
          </div>
        </section>

        {/* Articles Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="container-page">
            <div className="flex flex-col sm:flex-row items-end justify-between mb-10 md:mb-14">
              <div>
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
                  {t("home.articles.title")}
                </h2>
                <p className="text-sm text-foreground-600">{t("home.articles.subtitle")}</p>
              </div>
              <button
                onClick={() => {
                  navigate("/category/cross-border-business");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="mt-4 sm:mt-0 px-5 py-2.5 rounded-full border border-foreground-300 text-foreground-600 text-sm font-medium hover:border-primary-500 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                {t("cta.view_all")}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="flex flex-col gap-5">
              {articles.slice(0, 4).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}